import { ApiError, type PlatformLogin, type PlatformRole, type PlatformUser } from '@vlm/api';
import { prefs, toast } from '@vlm/ui';
import { useSyncExternalStore } from 'react';
import { translate } from '../i18n';
import { api, SESSION_KEY, sessionEvents, sessionStore } from './api';
import { queryClient } from './query';

/** checking — a stored session is being confirmed · unreachable — the API did not answer (the session is kept). */
export type AuthStatus = 'checking' | 'signedIn' | 'signedOut' | 'unreachable';

interface AuthState {
  status: AuthStatus;
  user: PlatformUser | null;
}

let state: AuthState = { status: sessionStore.get() ? 'checking' : 'signedOut', user: null };
const listeners = new Set<() => void>();
function setState(patch: Partial<AuthState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

export function useAuth<T>(select: (s: AuthState) => T): T {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => select(state),
    () => select(state),
  );
}

/** The user's saved language wins over this browser's. */
function adopt(user: PlatformUser) {
  prefs.setLang(user.language);
}

function clear() {
  queryClient.clear();
  setState({ status: 'signedOut', user: null });
}

/** On load: confirm the stored session by reading the user. */
export async function restoreSession(): Promise<void> {
  if (!sessionStore.get()) return setState({ status: 'signedOut', user: null });
  setState({ status: 'checking' });
  try {
    const user = await api.me.get();
    adopt(user);
    setState({ status: 'signedIn', user });
  } catch (error) {
    // refused (a dead session, a stopped account): sign out; away or failing (a restart, a 502): keep it
    if (error instanceof ApiError && error.status < 500) {
      sessionStore.set(null);
      clear();
    } else {
      setState({ status: 'unreachable' });
    }
  }
}

function signedIn(login: PlatformLogin) {
  sessionStore.set({ accessToken: login.accessToken, refreshToken: login.refreshToken });
  queryClient.clear();
  adopt(login.user);
  setState({ status: 'signedIn', user: login.user });
}

export async function signIn(username: string, password: string): Promise<PlatformUser> {
  const login = await api.auth.login(username, password);
  signedIn(login);
  return login.user;
}

/** A password change hands back new tokens: this tab stays signed in, every other session is out. */
export function adoptLogin(login: PlatformLogin) {
  sessionStore.set({ accessToken: login.accessToken, refreshToken: login.refreshToken });
  setState({ user: login.user });
}

/** Tokens simply expire; forgetting them is the sign-out. */
export function signOut() {
  sessionStore.set(null);
  clear();
}

export function setUser(user: PlatformUser) {
  setState({ user });
}

sessionEvents.expired = () => {
  const wasSignedIn = state.status === 'signedIn';
  clear();
  if (wasSignedIn) toast.info(translate('auth.expired'));
};

sessionEvents.refreshed = (login) => {
  if (state.status === 'signedIn') setState({ user: login.user });
};

// Another tab signed out (or in): follow it.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== SESSION_KEY) return;
    if (!event.newValue && state.status !== 'signedOut') clear();
    else if (event.newValue && state.status === 'signedOut') void restoreSession();
  });
}

export const useUser = () => useAuth((s) => s.user);

/** Whether the signed-in platform user has one of `roles` (the API enforces the same). */
export function useCan(roles: readonly PlatformRole[]): boolean {
  const role = useAuth((s) => s.user?.role);
  return role !== undefined && roles.includes(role);
}

export const ADMINS: readonly PlatformRole[] = ['ADMIN'];
